import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import StorySelector from './StorySelector';
import StoryReader from './StoryReader';

const AdventureStoryApp = ({ storyTemplates, formToken, initialStory }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [story, setStory] = useState(initialStory);
  const [error, setError] = useState(null);
  const [generationStatus, setGenerationStatus] = useState(initialStory?.status || 'idle');

  useEffect(() => {
    if (story?.status === 'generating') {
      const checkStatus = async () => {
        try {
          const response = await axios.get(`/adventure_stories/${story.id}/check_status`, {
            headers: {
              "X-CSRF-Token": formToken,
              "Content-Type": "application/json"
            }
          });

          if (response.data.status === 'completed') {
            setStory(prev => ({ ...prev, ...response.data }));
            setGenerationStatus('completed');
          } else {
            // Check again in 2 seconds
            setTimeout(checkStatus, 2000);
          }
        } catch (error) {
          console.error("Error checking status:", error);
          setError("Failed to check story status. Please refresh the page.");
        }
      };

      checkStatus();
    }
  }, [story?.id, formToken]);

  const handleStorySelection = async (templateId) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        '/adventure_stories/start',
        { template_id: templateId },
        {
          headers: {
            "X-CSRF-Token": formToken,
            "Content-Type": "application/json"
          }
        }
      );

      setStory(response.data);
      setGenerationStatus('generating');
    } catch (error) {
      console.error("Error starting story:", error);
      setError("Failed to start the story. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-500"></div>
        <p className="ml-4 text-xl text-indigo-700">Creating your adventure...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-8 bg-white rounded-xl shadow-lg">
        <h2 className="text-xl font-bold text-red-500">Error</h2>
        <p className="mt-2">{error}</p>
        <button
          onClick={() => setError(null)}
          className="mt-4 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (generationStatus === 'generating') {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-500"></div>
        <p className="ml-4 text-xl text-indigo-700">Generating your story...</p>
      </div>
    );
  }

  return story ? (
    <div className="max-w-6xl mx-auto p-8">
      <div className="flex flex-col md:flex-row gap-8">
        <div className="md:w-1/2 bg-white p-8 rounded-lg shadow-lg">
          <StoryReader
            story={story}
            formToken={formToken}
            onChoiceSelected={handleChoiceSelection}
          />
        </div>
        <div className="md:w-1/2 bg-white p-8 rounded-lg shadow-lg">
          {story.first_page?.image_url ? (
            <img
              src={story.first_page.image_url}
              alt="Story illustration"
              className="w-full h-auto rounded-lg"
            />
          ) : (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-500"></div>
              <p className="ml-4 text-xl text-indigo-700">Generating illustration...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  ) : (
    <StorySelector
      storyTemplates={storyTemplates}
      onSelect={handleStorySelection}
    />
  );
};

AdventureStoryApp.propTypes = {
  storyTemplates: PropTypes.array,
  formToken: PropTypes.string,
  initialStory: PropTypes.object
};

export default AdventureStoryApp;